// Quantity parsing, US -> Danish metric conversion, and pretty number formatting
// for ingredient lines.

const UNICODE_FRACTIONS = {
  '½': 1 / 2, '⅓': 1 / 3, '⅔': 2 / 3,
  '¼': 1 / 4, '¾': 3 / 4,
  '⅕': 1 / 5, '⅖': 2 / 5, '⅗': 3 / 5, '⅘': 4 / 5,
  '⅙': 1 / 6, '⅚': 5 / 6,
  '⅛': 1 / 8, '⅜': 3 / 8, '⅝': 5 / 8, '⅞': 7 / 8
}
const FRACTION_CHARS = '½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞'

// Imperial units we know how to convert. Order matters: longer phrases first
// so "fluid ounces" wins over "ounces".
const IMPERIAL_UNIT_RE = new RegExp(
  '^(' +
    'fluid\\s+ounces?|fl\\.?\\s*oz\\.?|' +
    'tablespoons?|tbsps?\\.?|' +
    'teaspoons?|tsps?\\.?|' +
    'cups?|' +
    'pints?|quarts?|gallons?|' +
    'pounds?|lbs?\\.?|' +
    'ounces?|ozs?\\.?' +
  ')(?=\\s|,|$|\\b)',
  'i'
)

// Convert a single imperial qty+unit to metric. Returns { value, unit }.
function convertImperial(value, unitRaw) {
  const u = unitRaw.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim()

  // Volume (Danish prefers spsk/tsk/dl)
  if (/^cups?$/.test(u))               return { value: value * 240,  unit: 'ml' }
  if (/^tbsps?$/.test(u) || /^tablespoons?$/.test(u))
    return { value: value, unit: 'spsk', skipRound: true }
  if (/^tsps?$/.test(u) || /^teaspoons?$/.test(u))
    return { value: value, unit: 'tsk', skipRound: true }
  if (/^(fluid ounces?|fl oz)$/.test(u)) return { value: value * 30,   unit: 'ml' }
  if (/^pints?$/.test(u))              return { value: value * 473,  unit: 'ml' }
  if (/^quarts?$/.test(u))             return { value: value * 946,  unit: 'ml' }
  if (/^gallons?$/.test(u))            return { value: value * 3785, unit: 'ml' }

  // Weight
  if (/^ozs?$/.test(u) || /^ounces?$/.test(u)) return { value: value * 28.35, unit: 'g' }
  if (/^lbs?$/.test(u) || /^pounds?$/.test(u)) return { value: value * 453.6, unit: 'g' }

  return null
}

// Round to kitchen-friendly increments.
function roundForKitchen(value, unit) {
  if (value == null || isNaN(value)) return value
  const abs = Math.abs(value)
  let step
  if (unit === 'ml') {
    if (abs < 20)  step = 1
    else if (abs < 100) step = 5
    else step = 10
  } else if (unit === 'g') {
    if (abs < 50)  step = 1
    else if (abs < 200) step = 5
    else if (abs < 500) step = 10
    else step = 25
  } else if (unit === 'dl') {
    step = 0.25
  } else if (unit === 'kg' || unit === 'l') {
    step = 0.05
  } else {
    return value
  }
  return Math.round(value / step) * step
}

// Promote ml -> dl / l, g -> kg if it reads more naturally.
function normalizeMetric(value, unit) {
  if (unit === 'ml' && value >= 1000) return { value: value / 1000, unit: 'l' }
  if (unit === 'ml' && value >= 100)  return { value: value / 100,  unit: 'dl' }
  if (unit === 'g'  && value >= 1000) return { value: value / 1000, unit: 'kg' }
  return { value, unit }
}

// Parse a quantity from the start of `text`.
// Returns { value: Number, length: Number } or null.
function parseQuantity(text) {
  // Strip leading spaces, but track the offset so the caller knows how much
  // of the original string we consumed.
  const lead = text.match(/^\s*/)[0]
  const rest = text.slice(lead.length)

  // 1. Mixed number with ASCII fraction: "1 1/2"
  let m = rest.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)/)
  if (m) return { value: +m[1] + (+m[2]) / (+m[3]), length: lead.length + m[0].length }

  // 2. Mixed number with unicode fraction: "1 ½"
  m = rest.match(new RegExp('^(\\d+)\\s+([' + FRACTION_CHARS + '])'))
  if (m) return { value: +m[1] + UNICODE_FRACTIONS[m[2]], length: lead.length + m[0].length }

  // 3. Number-followed-by-fraction no space: "1½"
  m = rest.match(new RegExp('^(\\d+)([' + FRACTION_CHARS + '])'))
  if (m) return { value: +m[1] + UNICODE_FRACTIONS[m[2]], length: lead.length + m[0].length }

  // 4. Plain ASCII fraction: "1/2"
  m = rest.match(/^(\d+)\s*\/\s*(\d+)/)
  if (m) return { value: (+m[1]) / (+m[2]), length: lead.length + m[0].length }

  // 5. Lone unicode fraction: "½"
  m = rest.match(new RegExp('^([' + FRACTION_CHARS + '])'))
  if (m) return { value: UNICODE_FRACTIONS[m[1]], length: lead.length + 1 }

  // 6. Decimal (accepts both "." and ",") : "1.5", "0,25"
  m = rest.match(/^(\d+(?:[.,]\d+)?)/)
  if (m) return { value: parseFloat(m[1].replace(',', '.')), length: lead.length + m[0].length }

  return null
}

// Format a number using nice fractions when possible, otherwise a comma decimal.
function formatNumber(value) {
  if (value == null || isNaN(value)) return ''
  if (value === 0) return '0'

  const rounded = Math.round(value * 10000) / 10000

  // Whole number?
  if (Math.abs(rounded - Math.round(rounded)) < 0.005) {
    return String(Math.round(rounded))
  }

  const sign = rounded < 0 ? '-' : ''
  const abs = Math.abs(rounded)
  const whole = Math.floor(abs)
  const frac = abs - whole

  const FRACTIONS = [
    [1 / 8, '⅛'], [1 / 4, '¼'], [1 / 3, '⅓'], [3 / 8, '⅜'],
    [1 / 2, '½'], [5 / 8, '⅝'], [2 / 3, '⅔'], [3 / 4, '¾'],
    [7 / 8, '⅞']
  ]

  // Match common fractions within ~3% tolerance.
  for (const [v, sym] of FRACTIONS) {
    if (Math.abs(frac - v) < 0.03) {
      return sign + (whole > 0 ? whole + sym : sym)
    }
  }

  // Fall back to a comma decimal. Trim trailing zeros.
  let dec
  if (abs < 1) dec = abs.toFixed(2)
  else if (abs < 10) dec = abs.toFixed(2)
  else dec = abs.toFixed(1)
  dec = dec.replace(/\.?0+$/, '')
  return sign + dec.replace('.', ',')
}

// True if the line contains any imperial unit we can convert.
function hasImperialUnit(text) {
  if (!text) return false
  return /\b(cups?|tablespoons?|tbsps?|teaspoons?|tsps?|fluid\s+ounces?|fl\.?\s*oz|pints?|quarts?|gallons?|ounces?|ozs?|pounds?|lbs?)\b/i.test(text)
}

// Parse + transform an ingredient line. Returns structured parts the caller
// can compose into plain text or HTML.
// Returns null when no quantity is found (line should be rendered as-is).
//
// Result shape:
//   { prefix, number, unit, rest, converted }
//
// Options:
//   scale   - multiplier to apply to the quantity (default 1)
//   metric  - if true, convert any imperial unit to Danish metric
function transformLine(line, { scale = 1, metric = false } = {}) {
  if (!line) return null

  // Find the FIRST quantity in the line. It is almost always at the start, but
  // some lines have a leading word like "ca." - allow up to ~20 chars of
  // non-digit lead-in. HTML tags at the start (e.g. "<b>") are tolerated by
  // the same lead-in budget.
  const qtyMatch = line.match(/^([^\d½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]{0,20}?)(?=[\d½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞])/)
  if (!qtyMatch) return null

  const prefix = qtyMatch[1]
  const remainder = line.slice(prefix.length)

  const qty = parseQuantity(remainder)
  if (!qty) return null

  let value = qty.value
  const afterQty = remainder.slice(qty.length).replace(/^\s+/, '')

  let unit = ''
  let rest = afterQty
  let converted = false
  const unitMatch = afterQty.match(IMPERIAL_UNIT_RE)

  if (unitMatch && metric) {
    const imp = convertImperial(value, unitMatch[1])
    if (imp) {
      value = imp.value * scale
      unit = imp.unit
      rest = afterQty.slice(unitMatch[0].length).replace(/^[\s,]+/, '')
      converted = true

      if (!imp.skipRound) {
        const rounded = roundForKitchen(value, unit)
        const norm = normalizeMetric(rounded, unit)
        value = roundForKitchen(norm.value, norm.unit)
        unit = norm.unit
      }
    } else {
      value *= scale
      unit = unitMatch[0]
      rest = afterQty.slice(unitMatch[0].length).replace(/^\s+/, '')
    }
  } else {
    // Not converting - just scale. Trim runaway decimals like 0.6666666.
    value *= scale
    if (scale !== 1 && Math.abs(value) >= 1) {
      value = Math.round(value * 100) / 100
    }
  }

  return {
    prefix,
    number: formatNumber(value),
    unit,
    rest,
    converted
  }
}

// Compose the result of transformLine() back into a string.
function partsToString(parts) {
  if (!parts) return ''
  return parts.prefix
    + parts.number
    + (parts.unit ? ' ' + parts.unit : '')
    + (parts.rest ? ' ' + parts.rest : '')
}

export default {
  parseQuantity,
  formatNumber,
  transformLine,
  partsToString,
  hasImperialUnit,
  convertImperial,
  roundForKitchen,
  normalizeMetric
}
